import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import HTMLFlipBook from 'react-pageflip';
import { useStory, STEPS } from '../../context/StoryContext';
import { api } from '../../services/api';
import { ChevronLeft, ChevronRight, Mail, RotateCcw } from 'lucide-react';
import BackArrow from '../ui/BackArrow';

// Rotate device overlay component for mobile portrait mode
const RotateDeviceOverlay = () => (
  <div 
    className="fixed inset-0 z-50 flex flex-col items-center justify-center"
    style={{ 
      backgroundColor: '#1a365d',
      padding: '24px'
    }}
  >
    <motion.div
      initial={{ rotate: 0 }}
      animate={{ rotate: -90 }}
      transition={{ 
        duration: 1.5, 
        repeat: Infinity, 
        repeatType: 'reverse',
        ease: 'easeInOut'
      }}
      className="mb-8"
    >
      <div 
        className="w-20 h-32 rounded-xl border-4 border-white flex items-center justify-center"
        style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
      >
        <div className="w-8 h-1 bg-white rounded-full" />
      </div>
    </motion.div>
    
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="text-center"
    >
      <RotateCcw className="w-12 h-12 text-white mx-auto mb-4" />
      <h2 
        className="text-2xl font-bold text-white mb-3"
        style={{ fontFamily: 'Georgia, serif' }}
      >
        Gira el dispositiu
      </h2>
      <p className="text-white/80 text-lg max-w-xs">
        Si us plau, posa el mòbil en horitzontal per gaudir del conte
      </p>
    </motion.div>
  </div>
);

const TURRIS_LOGO = '/logo-turris.svg';
const COVER_NOI_TEMPLATE = '/story/portada-noi.png';
const COVER_NOIA_TEMPLATE = '/story/portada-noia.png';

// Story pages configuration
// Each spread has: illustration + text
// textPosition: 'left' or 'right'
const STORY_PAGES_NOI = [
  {
    id: 1,
    illustration: '/story/page1-noi.png',
    title: 'El Pa de Sant Jordi',
    text: 'Un conte especial de Turris, on el protagonista és el nostre amic {NAME}',
    textPosition: 'right',
    showLogo: true,
    pageNumber: null
  },
  {
    id: 2,
    illustration: '/story/page2-noi.png',
    title: null,
    text: `En un petit poble, entre carrers de pedra i olor de pa acabat de fer, vivia el {NAME}.

Cada matí, abans que el sol pugés del tot, el {NAME} ajudava al forn del poble.

Pastar, esperar i enfornar era gairebé màgia.
I de totes les coses que sortien del forn, n'hi havia una que li semblava especial: el pa de primavera.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 3
  },
  {
    id: 3,
    illustration: '/story/page3-noi.png',
    title: null,
    text: `El pa de primavera no era un pa qualsevol.
Tenia formes i colors que recordaven la festa més bonica de la primavera.

Al poble, tothom l'esperava amb il·lusió.

Deien que era un pa fet per compartir, celebrar i somriure.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 5
  },
  {
    id: 4,
    illustration: '/story/page4-noi.png',
    title: null,
    text: `Però aquella vigília de l'inici de la primavera va passar alguna cosa inesperada.

Un drac va aparèixer al costat del castell.

No llançava foc, ni va fer malbé res, però rugia tan fort que tot el poble va tremolar.

La gent va tancar finestres, amagar cistelles i va deixar de preparar la festa.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 7
  },
  {
    id: 5,
    illustration: '/story/page5-noi.png',
    title: null,
    text: `Ningú sabia què fer.
Algunes persones deien que calia fer fora aquell drac, fins i tot alguns deien que calia matar-lo.

Altres pensaven que era massa perillós acostar-s'hi.

Però el {NAME} va tenir una idea diferent: potser el drac no havia vingut per espantar… potser havia vingut perquè necessitava alguna cosa.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 9
  },
  {
    id: 6,
    illustration: '/story/page6-noi.png',
    title: null,
    text: `Aquella nit, en silenci, el {NAME} va entrar al forn.

Va barrejar farina, aigua, tot d'ingredients especials, paciència i valor.

Després, va preparar el pa més bonic que havia fet mai.

Era un pa molt molt especial, fins i tot més especial i bonic que el pa de primavera. Un pa daurat per fora i ple d'afecte per dins.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 11
  },
  {
    id: 7,
    illustration: '/story/page7-noi.png',
    title: null,
    text: `Quan el pa va sortir del forn, l'aroma va omplir tota la fleca, tot el carrer, tot el poble.

Feia olor de festa i de dia gran.

El {NAME} portava el seu pa amb orgull, com qui porta la solució als problemes del món entre les seves mans.

Després va respirar profundament i va dirigir les seves passes cap al turó on era el drac.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 13
  },
  {
    id: 8,
    illustration: '/story/page8-noi.png',
    title: null,
    text: `El drac va mirar fixament el {NAME} i el pa que portava entre les mans i va llançar una bufada pel nas tant forta que va fer moure totes les fulles dels arbres.

Però el {NAME} no va sortir corrents.
Es va acostar a poc a poc, va apropar el pa al nas del drac tot dient:

—T'he portat una cosa —va dir amb veu tremolosa, però ferma.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 15
  },
  {
    id: 9,
    illustration: '/story/page9-noi.png',
    title: null,
    text: `El drac va ensumar el pa.
Després va fer un mos petit.
I després un altre de més gran.

Llavors va tancar els ulls i va fer un so suau, com si estigués content.

El drac no volia destruir el poble. Tenia gana i estava sol.

Havia olorat el pa des de molt lluny i havia seguit aquella aroma fins a arribar-hi.

El {NAME} va somriure. Ja ho havia entès tot.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 17
  },
  {
    id: 10,
    illustration: '/story/page10-noi.png',
    title: null,
    text: `Des d'una finestra del castell, la princesa ho havia vist tot.

Va baixar corrents a la plaça i va voler conèixer qui havia calmat el drac sense espasa, sense armadura i sense crits.

Quan va arribar al turó, va veure el {NAME} compartint pa amb el seu nou amic.

Aviat tot el poble es va acostar. Al principi amb compte. Després, amb sorpresa.

I al final, amb alegria, perquè el drac movia la cua, la princesa reia i el {NAME} repartia trossos d'aquell pa tant especial a tothom.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 19
  },
  {
    id: 11,
    illustration: '/story/page11-noi.png',
    title: null,
    text: `Aquella festa de primavera hi va haver de tot, roses i pa per celebrar i una història increïble que explicar.

El drac es va quedar al regne com a amic i guardià de la festa.

La princesa va dir que mai no oblidaria el que havia passat i va nomenar al {NAME} forner reial.

El {NAME} havia demostrat que de vegades el valor més gran no és lluitar, sinó compartir. I aquell pa que va fer, va acabar sent el Sant Jordi d'aquesta història.

El Pa de Sant Jordi del {NAME}.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 21
  },
  {
    id: 12,
    illustration: '/story/page-final-noi.png',
    title: null,
    text: `...fi

Feliç Sant Jordi!`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: null,
    isEndingPage: true
  },
  {
    id: 13,
    illustration: '/story/contraportada-noi.png',
    isBackCover: true
  }
];

const STORY_PAGES_NOIA = [
  {
    id: 1,
    illustration: '/story/page1-noia.png',
    title: 'El Pa de Sant Jordi',
    text: 'Un conte especial de Turris, on la protagonista és la nostra amiga {NAME}',
    textPosition: 'right',
    showLogo: true,
    pageNumber: null
  },
  {
    id: 2,
    illustration: '/story/page2-noia.png',
    title: null,
    text: `En un petit poble, entre carrers de pedra i olor de pa acabat de fer, vivia la {NAME}.

Cada matí, abans que el sol pugés del tot, la {NAME} ajudava al forn del poble.

Pastar, esperar i enfornar era gairebé màgia.
I de totes les coses que sortien del forn, n'hi havia una que li semblava especial: el pa de primavera.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 3
  },
  {
    id: 3,
    illustration: '/story/page3-noia.png',
    title: null,
    text: `El pa de primavera no era un pa qualsevol.
Tenia formes i colors que recordaven la festa més bonica de la primavera.

Al poble, tothom l'esperava amb il·lusió.

Deien que era un pa fet per compartir, celebrar i somriure.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 5
  },
  {
    id: 4,
    illustration: '/story/page4-noia.png',
    title: null,
    text: `Però aquella vigília de l'inici de la primavera va passar alguna cosa inesperada.

Un drac va aparèixer al costat del castell.

No llançava foc, ni va fer malbé res, però rugia tan fort que tot el poble va tremolar.

La gent va tancar finestres, amagar cistelles i va deixar de preparar la festa.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 7
  },
  {
    id: 5,
    illustration: '/story/page5-noia.png',
    title: null,
    text: `Ningú sabia què fer.
Algunes persones deien que calia fer fora aquell drac, fins i tot alguns deien que calia matar-lo.

Altres pensaven que era massa perillós acostar-s'hi.

Però la {NAME} va tenir una idea diferent: potser el drac no havia vingut per espantar… potser havia vingut perquè necessitava alguna cosa.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 9
  },
  {
    id: 6,
    illustration: '/story/page6-noia.png',
    title: null,
    text: `Aquella nit, en silenci, la {NAME} va entrar al forn.

Va barrejar farina, aigua, tot d'ingredients especials, paciència i valor.

Després, va preparar el pa més bonic que havia fet mai.

Era un pa molt molt especial, fins i tot més especial i bonic que el pa de primavera. Un pa daurat per fora i ple d'afecte per dins.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 11
  },
  {
    id: 7,
    illustration: '/story/page7-noia.png',
    title: null,
    text: `Quan el pa va sortir del forn, l'aroma va omplir tota la fleca, tot el carrer, tot el poble.

Feia olor de festa i de dia gran.

La {NAME} portava el seu pa amb orgull, com qui porta la solució als problemes del món entre les seves mans.

Després va respirar profundament i va dirigir les seves passes cap al turó on era el drac.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 13
  },
  {
    id: 8,
    illustration: '/story/page8-noia.png',
    title: null,
    text: `El drac va mirar fixament la {NAME} i el pa que portava entre les mans i va llançar una bufada pel nas tant forta que va fer moure totes les fulles dels arbres.

Però la {NAME} no va sortir corrents.
Es va acostar a poc a poc, va apropar el pa al nas del drac tot dient:

—T'he portat una cosa —va dir amb veu tremolosa, però ferma.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 15
  },
  {
    id: 9,
    illustration: '/story/page9-noia.png',
    title: null,
    text: `El drac va ensumar el pa.
Després va fer un mos petit.
I després un altre de més gran.

Llavors va tancar els ulls i va fer un so suau, com si estigués content.

El drac no volia destruir el poble. Tenia gana i estava sol.

Havia olorat el pa des de molt lluny i havia seguit aquella aroma fins a arribar-hi.

La {NAME} va somriure. Ja ho havia entès tot.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 17
  },
  {
    id: 10,
    illustration: '/story/page10-noia.png',
    title: null,
    text: `Des d'una finestra del castell, el príncep ho havia vist tot.

Va baixar corrents a la plaça i va voler conèixer qui havia calmat el drac sense espasa, sense armadura i sense crits.

Quan va arribar al turó, va veure la {NAME} compartint pa amb el seu nou amic.

Aviat tot el poble es va acostar. Al principi amb compte. Després, amb sorpresa.

I al final, amb alegria, perquè el drac movia la cua, el príncep reia i la {NAME} repartia trossos d'aquell pa tant especial a tothom.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 19
  },
  {
    id: 11,
    illustration: '/story/page11-noia.png',
    title: null,
    text: `Aquella festa de primavera hi va haver de tot, roses i pa per celebrar i una història increïble que explicar.

El drac es va quedar al regne com a amic i guardià de la festa.

El príncep va dir que mai no oblidaria el que havia passat i va nomenar a la {NAME} fornera reial.

La {NAME} havia demostrat que de vegades el valor més gran no és lluitar, sinó compartir. I aquell pa que va fer, va acabar sent el Sant Jordi d'aquesta història.

El Pa de Sant Jordi de la {NAME}.`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: 21
  },
  {
    id: 12,
    illustration: '/story/page-final-noia.png',
    title: null,
    text: `...fi

Feliç Sant Jordi!`,
    textPosition: 'left',
    showLogo: false,
    pageNumber: null,
    isEndingPage: true
  },
  {
    id: 13,
    illustration: '/story/contraportada-noia.png',
    isBackCover: true
  }
];

// Page component for the flipbook
const Page = React.forwardRef(({ children, className }, ref) => {
  return (
    <div ref={ref} className={`bg-white ${className || ''}`}>
      {children}
    </div>
  );
});

export default function PreviewScreen() {
  const { goToStep, goBack, protagonistName, protagonistType } = useStory();
  const [currentPage, setCurrentPage] = useState(0);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const bookRef = useRef(null);

  // Detect orientation and mobile device
  useEffect(() => {
    const checkOrientation = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setWindowSize({ width, height });
      setIsPortrait(height > width);
      setIsMobile(width < 768 || (height < 768 && width < 1024));
    };

    // Initial check
    checkOrientation();

    // Listen for resize and orientation change
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', () => {
      // Small delay to allow orientation change to complete
      setTimeout(checkOrientation, 100);
    });

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  const storyPages = protagonistType === 'noi' ? STORY_PAGES_NOI : STORY_PAGES_NOIA;
  // Total: 1 (front cover spread) + story pages + back cover is already in storyPages
  const totalSpreads = storyPages.length + 1; // +1 for front cover
  const totalPages = totalSpreads * 2;
  
  // Cover template based on protagonist type
  const coverTemplate = protagonistType === 'noi' ? COVER_NOI_TEMPLATE : COVER_NOIA_TEMPLATE;
  
  // Helper function to check if name starts with vowel (Catalan elision rules)
  const startsWithVowel = (name) => {
    if (!name) return false;
    const firstLetter = name.charAt(0).toLowerCase();
    return 'aeiouàèéíïòóúüh'.includes(firstLetter);
  };
  
  // Get correct Catalan article for the cover
  const getArticleForCover = () => {
    if (startsWithVowel(protagonistName)) {
      return "de l'"; // Elision for vowel-starting names
    }
    return protagonistType === 'noi' ? 'del ' : 'de la ';
  };
  
  const article = getArticleForCover();

  useEffect(() => {
    api.trackEvent('preview_viewed');
  }, []);

  const handleContinue = () => {
    api.trackEvent('preview_completed');
    goToStep(STEPS.FORM);
  };

  const personalizeText = (text) => {
    let result = text;
    const isVowel = startsWithVowel(protagonistName);
    
    if (isVowel) {
      // Replace article patterns when name starts with vowel
      result = result
        .replace(/\bel \{NAME\}/gi, (match) => {
          const isUpper = match.charAt(0) === 'E';
          return (isUpper ? "L'" : "l'") + protagonistName;
        })
        .replace(/\bla \{NAME\}/gi, (match) => {
          const isUpper = match.charAt(0) === 'L';
          return (isUpper ? "L'" : "l'") + protagonistName;
        })
        .replace(/\bdel \{NAME\}/gi, "de l'" + protagonistName)
        .replace(/\bde la \{NAME\}/gi, "de l'" + protagonistName)
        .replace(/\bal \{NAME\}/gi, "a l'" + protagonistName)
        .replace(/\ba la \{NAME\}/gi, "a l'" + protagonistName);
    }
    
    // Replace remaining {NAME} occurrences
    return result.replace(/{NAME}/g, protagonistName);
  };

  const nextPage = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipNext();
    }
  };

  const prevPage = () => {
    if (bookRef.current) {
      bookRef.current.pageFlip().flipPrev();
    }
  };

  const onFlip = (e) => {
    setCurrentPage(e.data);
  };

  // Check if we're in mobile landscape mode
  const isMobileLandscape = isMobile && !isPortrait;

  // Responsive text styles for mobile landscape vs desktop
  const getTextStyles = () => {
    if (isMobileLandscape) {
      return {
        // Title page (textPosition: 'right')
        titlePage: {
          padding: '16px 14px 12px 14px',
          title: { fontSize: '11px', marginBottom: '10px' },
          text: { fontSize: '8px', lineHeight: '1.5' },
          pageNumber: { fontSize: '8px' },
          logo: { height: '14px' }
        },
        // Story pages (textPosition: 'left')
        storyPage: {
          padding: '14px 12px 10px 12px',
          title: { fontSize: '9px', marginBottom: '8px' },
          text: { fontSize: '6.5px', lineHeight: '1.55' },
          pageNumber: { fontSize: '7px' },
          logo: { height: '12px' }
        },
        // Ending page
        endingPage: {
          padding: '16px',
          fiText: { fontSize: '12px', marginBottom: '12px' },
          santJordi: { fontSize: '14px' }
        }
      };
    }
    // Desktop/tablet styles (original)
    return {
      titlePage: {
        padding: '48px 44px 40px 44px',
        title: { fontSize: '24px', marginBottom: '24px' },
        text: { fontSize: '18px', lineHeight: '1.9' },
        pageNumber: { fontSize: '18px' },
        logo: { height: '36px' }
      },
      storyPage: {
        padding: '40px',
        title: { fontSize: '20px', marginBottom: '20px' },
        text: { fontSize: '15px', lineHeight: '1.85' },
        pageNumber: { fontSize: '16px' },
        logo: { height: '32px' }
      },
      endingPage: {
        padding: '40px',
        fiText: { fontSize: '28px', marginBottom: '32px' },
        santJordi: { fontSize: '36px' }
      }
    };
  };

  const textStyles = getTextStyles();

  // Generate all pages for the flipbook
  const generatePages = () => {
    const pages = [];
    
    // FRONT COVER: blank page (left) + personalized cover (right)
    pages.push(
      <Page key="blank-start">
        <div className="w-full h-full bg-white" />
      </Page>
    );
    pages.push(
      <Page key="front-cover">
        <div className="w-full h-full relative">
          <img
            src={coverTemplate}
            alt="Portada"
            className="w-full h-full object-cover"
          />
          {/* Personalized name overlay */}
          <div 
            className="absolute w-full flex justify-center px-2"
            style={{
              top: '27%',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <span
              className="font-bold whitespace-nowrap"
              style={{
                fontFamily: "'Fredoka One', 'Comic Sans MS', cursive",
                fontSize: 'clamp(1rem, 4vw, 1.8rem)',
                color: '#FFFFFF',
                textShadow: `
                  -2px -2px 0 #1a365d,
                  2px -2px 0 #1a365d,
                  -2px 2px 0 #1a365d,
                  2px 2px 0 #1a365d,
                  -2px 0 0 #1a365d,
                  2px 0 0 #1a365d,
                  0 -2px 0 #1a365d,
                  0 2px 0 #1a365d
                `,
                letterSpacing: '1px'
              }}
            >
              <span style={{ color: '#FFD700' }}>{article}</span>{protagonistName}
            </span>
          </div>
          {/* Footer text */}
          <div 
            className="absolute w-full flex justify-center px-4"
            style={{
              bottom: '3%',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <span
              className="text-center"
              style={{
                fontFamily: "'Georgia', serif",
                fontSize: 'clamp(0.5rem, 2vw, 0.9rem)',
                color: '#FFFFFF',
                fontStyle: 'italic',
                letterSpacing: '0.5px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              Una història per compartir al voltant del pa
            </span>
          </div>
        </div>
      </Page>
    );
    
    storyPages.forEach((spread, index) => {
      // Handle back cover - just a single full-page illustration
      if (spread.isBackCover) {
        pages.push(
          <Page key={`backcover-${index}`}>
            <div className="w-full h-full">
              <img
                src={spread.illustration}
                alt="Contraportada"
                className="w-full h-full object-cover"
              />
            </div>
          </Page>
        );
        return;
      }
      
      if (spread.textPosition === 'right') {
        // Illustration left, text right
        pages.push(
          <Page key={`illust-${index}`}>
            <div className="w-full h-full">
              <img
                src={spread.illustration}
                alt={`Ilustració ${spread.id}`}
                className="w-full h-full object-cover"
              />
            </div>
          </Page>
        );
        pages.push(
          <Page key={`text-${index}`}>
            <div 
              className="w-full h-full"
              style={{ 
                padding: textStyles.titlePage.padding,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {spread.title && (
                  <h2 
                    style={{ 
                      color: '#1a365d',
                      fontSize: textStyles.titlePage.title.fontSize,
                      fontFamily: 'Georgia, serif',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      marginBottom: textStyles.titlePage.title.marginBottom
                    }}
                  >
                    {spread.title}
                  </h2>
                )}
                <p 
                  style={{ 
                    color: '#222',
                    fontSize: textStyles.titlePage.text.fontSize,
                    lineHeight: textStyles.titlePage.text.lineHeight,
                    fontWeight: '500',
                    textAlign: 'center',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {personalizeText(spread.text)}
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: isMobileLandscape ? '6px' : '20px' }}>
                {spread.pageNumber ? (
                  <span style={{ color: '#222', fontSize: textStyles.titlePage.pageNumber.fontSize, fontWeight: '600' }}>
                    {spread.pageNumber}
                  </span>
                ) : <span />}
                {spread.showLogo && (
                  <img src={TURRIS_LOGO} alt="Turris" style={{ height: textStyles.titlePage.logo.height }} />
                )}
              </div>
            </div>
          </Page>
        );
      } else {
        // Text left, illustration right
        // Check if this is the ending page (special styling)
        if (spread.isEndingPage) {
          pages.push(
            <Page key={`text-${index}`}>
              <div 
                style={{ 
                  width: '100%',
                  height: '100%',
                  padding: textStyles.endingPage.padding,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <p 
                    style={{ 
                      color: '#222',
                      fontSize: textStyles.endingPage.fiText.fontSize,
                      fontWeight: '500',
                      marginBottom: textStyles.endingPage.fiText.marginBottom
                    }}
                  >
                    ...fi
                  </p>
                  <p 
                    style={{ 
                      color: '#222',
                      fontSize: textStyles.endingPage.santJordi.fontSize,
                      fontWeight: 'bold',
                      fontFamily: 'Georgia, serif'
                    }}
                  >
                    Feliç Sant Jordi!
                  </p>
                </div>
              </div>
            </Page>
          );
        } else {
          pages.push(
            <Page key={`text-${index}`}>
              <div 
                style={{ 
                  width: '100%',
                  height: '100%',
                  padding: textStyles.storyPage.padding,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', overflow: 'hidden' }}>
                  {spread.title && (
                    <h2 
                      style={{ 
                        color: '#1a365d',
                        fontSize: textStyles.storyPage.title.fontSize,
                        fontFamily: 'Georgia, serif',
                        fontWeight: 'bold',
                        textAlign: 'left',
                        marginBottom: textStyles.storyPage.title.marginBottom,
                        flexShrink: 0
                      }}
                    >
                      {spread.title}
                    </h2>
                  )}
                  <p 
                    style={{ 
                      color: '#222',
                      fontSize: textStyles.storyPage.text.fontSize,
                      lineHeight: textStyles.storyPage.text.lineHeight,
                      fontWeight: '500',
                      textAlign: 'left',
                      whiteSpace: 'pre-line',
                      overflow: 'hidden'
                    }}
                  >
                    {personalizeText(spread.text)}
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: isMobileLandscape ? '4px' : '16px', flexShrink: 0 }}>
                  {spread.pageNumber ? (
                    <span style={{ color: '#222', fontSize: textStyles.storyPage.pageNumber.fontSize, fontWeight: '600' }}>
                      {spread.pageNumber}
                    </span>
                  ) : <span />}
                  {spread.showLogo && (
                    <img src={TURRIS_LOGO} alt="Turris" style={{ height: textStyles.storyPage.logo.height }} />
                  )}
                </div>
              </div>
            </Page>
          );
        }
        pages.push(
          <Page key={`illust-${index}`}>
            <div className="w-full h-full">
              <img
                src={spread.illustration}
                alt={`Ilustració ${spread.id}`}
                className="w-full h-full object-cover"
              />
            </div>
          </Page>
        );
      }
    });
    
    return pages;
  };

  // Calculate book dimensions based on screen size
  const getBookDimensions = () => {
    // Landscape mobile: optimize for horizontal viewing
    if (isMobile && !isPortrait) {
      const availableHeight = windowSize.height - 50; // Very minimal header
      const pageHeight = Math.min(Math.max(availableHeight * 0.92, 280), 360);
      const pageWidth = Math.floor(pageHeight * 0.71); // Maintain aspect ratio
      return {
        width: pageWidth,
        height: pageHeight,
        minWidth: Math.floor(pageWidth * 0.85),
        maxWidth: Math.floor(pageWidth * 1.2),
        minHeight: Math.floor(pageHeight * 0.85),
        maxHeight: Math.floor(pageHeight * 1.2)
      };
    }
    // Desktop/tablet: use original dimensions
    return {
      width: 550,
      height: 770,
      minWidth: 400,
      maxWidth: 650,
      minHeight: 560,
      maxHeight: 910
    };
  };

  const bookDimensions = getBookDimensions();

  // Show rotate overlay for mobile in portrait mode
  if (isMobile && isPortrait) {
    return <RotateDeviceOverlay />;
  }

  // Landscape mobile layout - horizontal arrangement
  if (isMobile && !isPortrait) {
    return (
      <div 
        className="h-screen flex flex-col overflow-hidden" 
        style={{ backgroundColor: '#F5F5F5' }}
      >
        {/* Compact header */}
        <header className="flex items-center justify-between px-3 py-1">
          <BackArrow onClick={goBack} size="small" />
          <span className="text-xs font-medium" style={{ color: '#666' }}>
            {Math.floor(currentPage / 2) + 1} / {totalSpreads}
          </span>
          <div className="w-6" />
        </header>

        {/* Main content - horizontal layout */}
        <main className="flex-1 flex items-center justify-center gap-4 px-4">
          {/* Book viewer */}
          <div className="relative flex items-center">
            {/* Prev button */}
            {currentPage > 0 && (
              <button
                data-testid="prev-page"
                onClick={prevPage}
                className="w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10 mr-2"
              >
                <ChevronLeft className="w-4 h-4" style={{ color: '#1a365d' }} />
              </button>
            )}
            
            <HTMLFlipBook
              ref={bookRef}
              width={bookDimensions.width}
              height={bookDimensions.height}
              size="stretch"
              minWidth={bookDimensions.minWidth}
              maxWidth={bookDimensions.maxWidth}
              minHeight={bookDimensions.minHeight}
              maxHeight={bookDimensions.maxHeight}
              showCover={false}
              mobileScrollSupport={true}
              onFlip={onFlip}
              className="shadow-2xl"
              startPage={0}
              drawShadow={true}
              flippingTime={600}
              usePortrait={false}
              autoSize={true}
              maxShadowOpacity={0.5}
              showPageCorners={true}
              disableFlipByClick={false}
            >
              {generatePages()}
            </HTMLFlipBook>

            {/* Next button */}
            {currentPage < totalPages - 2 && (
              <button
                data-testid="next-page"
                onClick={nextPage}
                className="w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10 ml-2"
              >
                <ChevronRight className="w-4 h-4" style={{ color: '#1a365d' }} />
              </button>
            )}
          </div>

          {/* CTA Button - right side */}
          <motion.button
            data-testid="receive-story-button"
            onClick={handleContinue}
            className="px-4 py-3 rounded-full text-sm font-semibold text-white shadow-lg flex flex-col items-center gap-1 transition-all duration-300"
            style={{ 
              backgroundColor: '#D32F2F',
              boxShadow: '0 6px 20px rgba(211, 47, 47, 0.3)',
              minWidth: '130px'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Mail className="w-5 h-5" />
            <span className="text-xs leading-tight text-center">Rebre el conte<br/>al meu email</span>
          </motion.button>
        </main>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col" 
      style={{ backgroundColor: '#F5F5F5' }}
    >
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <BackArrow onClick={goBack} />
        <span className="text-sm font-medium" style={{ color: '#666' }}>
          Pàgina {Math.floor(currentPage / 2) + 1} de {totalSpreads}
        </span>
        <div className="w-10" />
      </header>

      {/* Book viewer */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-6">
        <div className="relative">
          <HTMLFlipBook
            ref={bookRef}
            width={bookDimensions.width}
            height={bookDimensions.height}
            size="stretch"
            minWidth={bookDimensions.minWidth}
            maxWidth={bookDimensions.maxWidth}
            minHeight={bookDimensions.minHeight}
            maxHeight={bookDimensions.maxHeight}
            showCover={false}
            mobileScrollSupport={true}
            onFlip={onFlip}
            className="shadow-2xl"
            style={{}}
            startPage={0}
            drawShadow={true}
            flippingTime={600}
            usePortrait={false}
            startZIndex={0}
            autoSize={true}
            maxShadowOpacity={0.5}
            showPageCorners={true}
            disableFlipByClick={false}
          >
            {generatePages()}
          </HTMLFlipBook>

          {/* Navigation buttons */}
          {currentPage > 0 && (
            <button
              data-testid="prev-page"
              onClick={prevPage}
              className="absolute left-0 sm:-left-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
            >
              <ChevronLeft className="w-6 h-6" style={{ color: '#1a365d' }} />
            </button>
          )}
          
          {currentPage < totalPages - 2 && (
            <button
              data-testid="next-page"
              onClick={nextPage}
              className="absolute right-0 sm:-right-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
            >
              <ChevronRight className="w-6 h-6" style={{ color: '#1a365d' }} />
            </button>
          )}
        </div>

        {/* Page indicator dots */}
        <div className="flex gap-2 mt-6">
          {Array.from({ length: totalSpreads }).map((_, idx) => (
            <div
              key={idx}
              className={`w-3 h-3 rounded-full transition-all ${
                Math.floor(currentPage / 2) === idx ? 'scale-125' : ''
              }`}
              style={{
                backgroundColor: Math.floor(currentPage / 2) === idx ? '#D32F2F' : '#E0E0E0'
              }}
            />
          ))}
        </div>

        {/* CTA Button */}
        <motion.button
          data-testid="receive-story-button"
          onClick={handleContinue}
          className="mt-6 px-8 sm:px-10 py-4 rounded-full text-base sm:text-lg font-semibold text-white shadow-lg flex items-center gap-2 transition-all duration-300 hover:-translate-y-1"
          style={{ 
            backgroundColor: '#D32F2F',
            boxShadow: '0 8px 24px rgba(211, 47, 47, 0.3)'
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Mail className="w-5 h-5" />
          Rebre el conte al meu email
        </motion.button>
      </main>
    </div>
  );
}
